import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import classNames from "classnames";
import { usePlanzonePermissions } from "@components/permissions";

import { FolderIcon } from "@pages/utilityPage/navigation/folderIcon";
import { ReportTemplateIcon } from "@pages/utilityPage/navigation/reportTemplateIcon";
import { ZonesList } from "@common/layout/zonesList";
import { UseCases } from "@planzone/core";
import { useLocation } from "react-router";
import { useModal } from "@root/client/modal";
import { usePreferences } from "@components/preferences";
import { ProjectModelsIcon } from "@common/assets/projectModelsIcon";
import { EventIcon } from "@common/eventIcon";
import { SynthesisIcon } from "./synthesisIcon";
import { ManagementIcon } from "./managementIcon";
import { ProjectPortfolioIcon } from "@common/projectPortfolioIcon";
import { RestrictedArea } from "@scenes/restrictedArea";
import { Props } from "./types";
import { ManagementIconArrowBottom } from "./managementIconArrowBottom";
import { ManagementIconArrowTop } from "./managementIconArrowTop";
import { MembersIcon } from "./membersIcon";
import { ObsIcon } from "@common/assets/obsIcon";
import { CostProfilesIcon } from "./costProfilesIcon";
import { CapacityIcon } from "./capacityIcon";
import { WorkloadIcon } from "./workloadIcon";
import { OccupancyIcon } from "./occupancyIcon";
import { UtilityIcon } from "./utilityIcon";
import { ZonesIcon } from "./zonesIcon";
import { StrategyIcon } from "./strategyIcon";
import { SecondaryCriteriaIcon } from "./secondaryCriteriaIcon";
import { PlusIcon } from "./plusIcon";
import { ExportsIcon } from "./exportsIcon";
import { DocumentsIcon } from "./documentsIcon";
import styles from "./styles.scss";

function Navigation(props: Props) {
  const { t } = useTranslation();
  const { isAdministrator } = usePlanzonePermissions();
  const push = useNavigate();
  const { pathname } = useLocation();
  const { pushView } = useModal();
  const { state } = usePreferences();
  const clear = () => {
    props.setIsSubMenuShown(false);
    props.setIsUtilitiesShown(false);
    props.setIsStrategyShown(false);
  };

  return (
    <ul
      className={classNames(styles.navigation, {
        [styles.closed]: !props.isOpen,
      })}
    >
      <RestrictedArea areas={["personalTimesheet", "personalSynthesis"]}>
        {({ canAccessPersonalSynthesis, canAccessPersonalTimesheet }) =>
          (canAccessPersonalSynthesis || canAccessPersonalTimesheet) && (
            <li onClick={clear} className={styles.item}>
              <NavLink
                to="/"
                className={({ isActive }) =>
                  classNames(styles.link, { [styles.selectedLink]: isActive })
                }
              >
                <SynthesisIcon className={styles.icon} />
                <p className={styles.name}>
                  {t("layout.navigation.synthesis")}
                </p>
              </NavLink>
            </li>
          )
        }
      </RestrictedArea>
      <RestrictedArea
        areas={[
          "resources",
          "costProfiles",
          "capacity",
          "workload",
          "occupancy",
        ]}
      >
        {({
          canAccessResources,
          canAccessCostProfiles,
          canAccessCapacity,
          canAccessWorkload,
          canAccessOccupancy,
        }) =>
          (canAccessResources ||
            canAccessCostProfiles ||
            canAccessCapacity ||
            canAccessWorkload ||
            canAccessOccupancy) && (
            <li className={styles.item}>
              <div
                className={styles.link}
                onClick={() => {
                  props.setIsSubMenuShown(!props.isSubMenuShown);
                  props.setIsUtilitiesShown(false);
                  props.setIsStrategyShown(false);
                  !props.isSubMenuShown &&
                    push(
                      canAccessResources
                        ? "/members"
                        : canAccessCostProfiles
                          ? "/cost-profiles"
                          : canAccessCapacity
                            ? "/capacity"
                            : canAccessWorkload
                              ? "/workload"
                              : "/occupancy",
                    );
                }}
              >
                <ManagementIcon
                  className={classNames(styles.icon, styles.managementIcon)}
                />
                <div className={styles.mainDropdown}>
                  <p className={styles.name}>
                    {t("layout.navigation.management")}
                  </p>
                  <div className={styles.push}>
                    {!props.isSubMenuShown ? (
                      <ManagementIconArrowBottom className={styles.arrowIcon} />
                    ) : (
                      <ManagementIconArrowTop className={styles.arrowIcon} />
                    )}
                  </div>
                </div>
              </div>
              {props.isSubMenuShown && canAccessResources && (
                <div className={styles.subManagementMenu}>
                  <NavLink
                    to="/members"
                    className={({ isActive }) =>
                      classNames(styles.subLink, styles.subMenuItems, {
                        [styles.selectedLink]: isActive,
                      })
                    }
                  >
                    <MembersIcon className={styles.icon} />
                    <p className={styles.resourcesName}>
                      {t("layout.navigation.resources")}
                    </p>
                  </NavLink>
                </div>
              )}
              {props.isSubMenuShown && canAccessResources && (
                <div className={styles.subManagementMenu}>
                  <NavLink
                    to="/organizational-units"
                    className={({ isActive }) =>
                      classNames(styles.subLink, styles.subMenuItems, {
                        [styles.selectedLink]: isActive,
                      })
                    }
                  >
                    <ObsIcon className={styles.icon} />
                    <p className={styles.resourcesName}>
                      {t("layout.navigation.obs")}
                    </p>
                  </NavLink>
                </div>
              )}
              {props.isSubMenuShown && canAccessCostProfiles && (
                <div className={styles.subManagementMenu}>
                  <NavLink
                    to="/cost-profiles"
                    className={({ isActive }) =>
                      classNames(styles.subLink, styles.subMenuItems, {
                        [styles.selectedLink]: isActive,
                      })
                    }
                  >
                    <CostProfilesIcon className={styles.icon} />
                    <p className={styles.resourcesName}>
                      {t("layout.navigation.costProfiles")}
                    </p>
                  </NavLink>
                </div>
              )}
              {props.isSubMenuShown && canAccessCapacity && (
                <div className={styles.subManagementMenu}>
                  <NavLink
                    to="/capacity"
                    className={({ isActive }) =>
                      classNames(styles.subLink, styles.subMenuItems, {
                        [styles.selectedLink]: isActive,
                      })
                    }
                  >
                    <CapacityIcon className={styles.icon} />
                    <p className={styles.resourcesName}>
                      {t("layout.navigation.capacity")}
                    </p>
                  </NavLink>
                </div>
              )}
              {props.isSubMenuShown && canAccessWorkload && (
                <div className={styles.subManagementMenu}>
                  <NavLink
                    to="/workload"
                    className={({ isActive }) =>
                      classNames(styles.subLink, styles.subMenuItems, {
                        [styles.selectedLink]: isActive,
                      })
                    }
                  >
                    <WorkloadIcon className={styles.icon} />
                    <p className={styles.resourcesName}>
                      {t("layout.navigation.workload")}
                    </p>
                  </NavLink>
                </div>
              )}
              {props.isSubMenuShown && canAccessOccupancy && (
                <div className={styles.subManagementMenu}>
                  <NavLink
                    to="/occupancy"
                    className={({ isActive }) =>
                      classNames(styles.subLink, styles.subMenuItems, {
                        [styles.selectedLink]: isActive,
                      })
                    }
                  >
                    <OccupancyIcon className={styles.icon} />
                    <p className={styles.resourcesName}>
                      {t("layout.navigation.occupancy")}
                    </p>
                  </NavLink>
                </div>
              )}
            </li>
          )
        }
      </RestrictedArea>
      <RestrictedArea
        areas={[
          "projectTemplates",
          "projectReportsTemplate",
          "projectStatuses",
          "secondaryCriteriaTemplate",
          "strategicAxes",
        ]}
      >
        {({
          canAccessProjectTemplates,
          canAccessProjectReportsTemplate,
          canAccessProjectStatuses,
          canAccessSecondaryCriteriaTemplate,
          canAccessStrategicAxes,
        }) =>
          (canAccessProjectTemplates ||
            canAccessProjectReportsTemplate ||
            canAccessProjectStatuses ||
            canAccessSecondaryCriteriaTemplate ||
            canAccessStrategicAxes) && (
            <li className={styles.item}>
              <div
                className={styles.link}
                onClick={() => {
                  props.setIsSubMenuShown(false);
                  props.setIsStrategyShown(false);
                  props.setIsUtilitiesShown(!props.isUtilitiesShown);
                }}
              >
                <UtilityIcon className={styles.managementIcon} />
                <div className={styles.mainDropdown}>
                  <p className={styles.name}>
                    {t("layout.navigation.utilities")}
                  </p>
                  <div className={styles.push}>
                    {!props.isUtilitiesShown ? (
                      <ManagementIconArrowBottom className={styles.arrowIcon} />
                    ) : (
                      <ManagementIconArrowTop className={styles.arrowIcon} />
                    )}
                  </div>
                </div>
              </div>
              {props.isUtilitiesShown && (
                <div>
                  {canAccessProjectTemplates && (
                    <div className={styles.subManagementMenu}>
                      <NavLink
                        to="/utilities/project-models"
                        className={({ isActive }) =>
                          classNames(styles.subLink, styles.subMenuItems, {
                            [styles.selectedLink]: isActive,
                          })
                        }
                      >
                        <ProjectModelsIcon className={styles.managementIcon} />
                        <p className={styles.resourcesName}>
                          {t("utilityPage.navigation.projectModels")}
                        </p>
                      </NavLink>
                    </div>
                  )}
                  {canAccessSecondaryCriteriaTemplate && (
                    <div className={styles.subManagementMenu}>
                      <NavLink
                        to="/utilities/criteria"
                        className={({ isActive }) =>
                          classNames(styles.subLink, styles.subMenuItems, {
                            [styles.selectedLink]: isActive,
                          })
                        }
                      >
                        <SecondaryCriteriaIcon
                          className={styles.managementIcon}
                        />
                        <p className={styles.resourcesName}>
                          {t(
                            "utilityPage.navigation.secondaryCriteriaTemplate",
                          )}
                        </p>
                      </NavLink>
                    </div>
                  )}
                  {canAccessProjectReportsTemplate && (
                    <div className={styles.subManagementMenu}>
                      <NavLink
                        to="/utilities/reports"
                        className={({ isActive }) =>
                          classNames(styles.subLink, styles.subMenuItems, {
                            [styles.selectedLink]: isActive,
                          })
                        }
                      >
                        <ReportTemplateIcon className={styles.managementIcon} />
                        <p className={styles.resourcesName}>
                          {t("utilityPage.navigation.projectReportTemplate")}
                        </p>
                      </NavLink>
                    </div>
                  )}
                  {canAccessProjectStatuses && (
                    <div className={styles.subManagementMenu}>
                      <NavLink
                        to="/utilities"
                        className={({ isActive }) =>
                          classNames(styles.subLink, styles.subMenuItems, {
                            [styles.selectedLink]: isActive,
                          })
                        }
                        end
                      >
                        <FolderIcon className={styles.managementIcon} />
                        <p className={styles.resourcesName}>
                          {t("utilityPage.navigation.projects")}
                        </p>
                      </NavLink>
                    </div>
                  )}
                  {canAccessStrategicAxes && (
                    <div className={styles.subManagementMenu}>
                      <NavLink
                        to="/utilities/strategic-axes"
                        className={({ isActive }) =>
                          classNames(styles.subLink, styles.subMenuItems, {
                            [styles.selectedLink]: isActive,
                          })
                        }
                        end
                      >
                        <StrategyIcon className={styles.managementIcon} />
                        <p className={styles.resourcesName}>
                          {t("strategicAxesPage.navigation")}
                        </p>
                      </NavLink>
                    </div>
                  )}
                </div>
              )}
            </li>
          )
        }
      </RestrictedArea>
      <RestrictedArea areas={["portfolios"]}>
        {({ canAccessPortfolios }) =>
          canAccessPortfolios && (
            <li className={styles.item} onClick={clear}>
              <NavLink
                to="/portfolios"
                className={({ isActive }) =>
                  classNames(styles.link, { [styles.selectedLink]: isActive })
                }
              >
                <ProjectPortfolioIcon
                  className={classNames(styles.icon, styles.eventIcon)}
                />
                <p className={styles.name}>
                  {t("layout.navigation.portfolios")}
                </p>
              </NavLink>
            </li>
          )
        }
      </RestrictedArea>
      <RestrictedArea areas={["calendar"]}>
        {({ canAccessCalendar }) =>
          canAccessCalendar && (
            <li onClick={clear} className={styles.item}>
              <NavLink
                to="/calendar"
                className={({ isActive }) =>
                  classNames(styles.link, { [styles.selectedLink]: isActive })
                }
              >
                <EventIcon
                  className={classNames(styles.icon, styles.eventIcon)}
                />
                <p className={styles.name}>{t("layout.navigation.calendar")}</p>
              </NavLink>
            </li>
          )
        }
      </RestrictedArea>
      <RestrictedArea areas={["documents"]}>
        {({ canAccessDocuments }) =>
          canAccessDocuments && (
            <li onClick={clear} className={styles.item}>
              <NavLink
                to="/documents"
                className={({ isActive }) =>
                  classNames(styles.link, { [styles.selectedLink]: isActive })
                }
              >
                <DocumentsIcon
                  className={classNames(styles.icon, styles.eventIcon)}
                />
                <p className={styles.name}>
                  {t("layout.navigation.documents")}
                </p>
              </NavLink>
            </li>
          )
        }
      </RestrictedArea>
      <RestrictedArea areas={["exports"]}>
        {({ canAccessExports }) =>
          canAccessExports && (
            <li onClick={clear} className={styles.item}>
              <NavLink
                to="/exports"
                className={({ isActive }) =>
                  classNames(styles.link, { [styles.selectedLink]: isActive })
                }
              >
                <ExportsIcon
                  className={classNames(styles.icon, styles.eventIcon)}
                />
                <p className={styles.name}>
                  {t("layout.navigation.exportReports")}
                </p>
              </NavLink>
            </li>
          )
        }
      </RestrictedArea>
      <div className={styles.border} />
      <li
        className={classNames(styles.item, {
          [styles.openedZones]: props.isZonesShown,
        })}
      >
        <div className={styles.zones}>
          {isAdministrator &&
            (state.isLayoutSidebarMinimized ? (
              <div
                className={styles.buttonCreateZoneCollapse}
                onClick={() =>
                  pushView({ key: UseCases.createZone, data: props.planzone })
                }
              >
                <PlusIcon className={styles.iconAdd} />
              </div>
            ) : (
              <div
                className={styles.buttonCreateZone}
                onClick={() =>
                  pushView({ key: UseCases.createZone, data: props.planzone })
                }
              >
                {t("zones.addZone")}
              </div>
            ))}
          <div className={styles.buttonRedirectZonePage}>
            <NavLink
              to="/zones"
              className={({ isActive }) =>
                classNames(styles.redirectZonePage, {
                  [styles.activeLink]: isActive,
                })
              }
              end
            >
              <ZonesIcon className={styles.iconZone} />
              {!state.isLayoutSidebarMinimized && (
                <div
                  className={pathname === "/zones" ? styles.selectAllZone : ""}
                >
                  {t("zones.allZone")}
                </div>
              )}
            </NavLink>
          </div>
          <div className={styles.zoneList}>
            <ZonesList
              planzone={props.planzone}
              selectedZone={props.zone}
              isSideBarOpen={!state.isLayoutSidebarMinimized}
              currentPath={pathname}
              addProject={(zone) =>
                pushView({
                  key: UseCases.createProject,
                  data: { zone },
                })
              }
              setIsSubMenuShown={props.setIsSubMenuShown}
              setIsUtilitiesShown={props.setIsUtilitiesShown}
            />
          </div>
        </div>
      </li>
    </ul>
  );
}

export { Navigation };
